/** @format */
import { Divider } from '@mpb-com/feather/components/atoms/divider';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import {
    HeadingAlignment,
    HeadingSize,
} from '@mpb-com/feather/components/atoms/heading/types';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import {
    AvailableMarket,
    defaultMarket,
    primaryMarketsInMultiMarketCountry,
} from '@toucan/config/markets';

import { trackViaDataLayer } from '@toucan/helpers/analytics';
import { normalizeIsoCode } from '@toucan/helpers/language';

import { MarketContext } from '@toucan/client/global/market';

import { MarketOption } from './components/market-option';
import { styles } from './market-confirmation-modal.styles';
import {
    MarketConfirmationModalProps,
    StyledMarketConfirmationModalProps,
} from './types';

const StyledMarketConfirmationModal = styled(
    'div'
)<StyledMarketConfirmationModalProps>`
    ${styles}
`;

export const MarketConfirmationModal: FunctionComponent<
    MarketConfirmationModalProps
> = ({ suggestedMarketCodes, onClose }: MarketConfirmationModalProps) => {
    const { t } = useTranslation();
    const { activeMarket, availableMarkets } = useContext(MarketContext);

    const suggestedMarkets = availableMarkets
        .filter(({ code }) => suggestedMarketCodes.includes(code))
        .sort((marketA, marketB) => {
            const isMarketAPrimary = Boolean(
                // @ts-ignore - Element implicitly has an any type - Touch and Change
                primaryMarketsInMultiMarketCountry[marketA.code]
            );

            if (isMarketAPrimary) {
                return -1;
            }

            const isMarketBPrimary = Boolean(
                // @ts-ignore - Element implicitly has an any type - Touch and Change
                primaryMarketsInMultiMarketCountry[marketB.code]
            );

            if (isMarketBPrimary) {
                return 1;
            }

            return 0;
        });

    const suggestedMarketLanguages = suggestedMarkets?.filter(
        ({ language }) => language !== activeMarket.language
    );

    const suggestedMarket = suggestedMarketLanguages[0];

    const suggestedMarketLanguage = normalizeIsoCode(
        suggestedMarket?.iso ?? defaultMarket.iso
    );

    const showTranslations = Boolean(
        suggestedMarket && suggestedMarket.language !== activeMarket.language
    );

    const shouldShowDivider = suggestedMarketCodes.length > 1;

    const suggestedMarketOnClickHandler = (
        suggestedMarket: AvailableMarket
    ) => {
        return (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();

            onClose(suggestedMarket.code);

            trackViaDataLayer({
                event: 'NAVIGATE_TO_MARKET',
                location: 'POPUP',
            });

            window.location.href = `/${suggestedMarket.slug}`;
        };
    };

    return (
        <StyledMarketConfirmationModal
            $showTranslations={showTranslations}
            data-testid="market-confirmation-modal"
        >
            <Heading
                size={HeadingSize.H3}
                alignment={HeadingAlignment.CENTER}
                className="heading"
            >
                {t('Where would you like to go?', { ns: 'global' })}
            </Heading>
            {showTranslations && (
                <div
                    className="heading-translation"
                    data-testid="market-confirmation-modal__heading-translation"
                >
                    {t('Where would you like to go?', {
                        lng: suggestedMarketLanguage,
                        ns: 'global',
                    })}
                </div>
            )}
            <nav className="markets">
                <MarketOption
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                        event.preventDefault();
                        onClose(activeMarket.code);
                    }}
                    showTranslations={showTranslations}
                    market={activeMarket}
                    suggestedMarketLanguage={suggestedMarketLanguage}
                />

                {shouldShowDivider && (
                    <Divider
                        className="markets-divider"
                        data-testid="market-confirmation-modal__divider"
                    />
                )}

                {suggestedMarkets.map((suggestedMarket) => (
                    <MarketOption
                        key={suggestedMarket.code}
                        onClick={suggestedMarketOnClickHandler(suggestedMarket)}
                        showTranslations={showTranslations}
                        market={suggestedMarket}
                        suggestedMarketLanguage={suggestedMarketLanguage}
                    />
                ))}
            </nav>
        </StyledMarketConfirmationModal>
    );
};
